import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { BellIcon } from '@heroicons/react/outline';
import { HubConnection} from '@microsoft/signalr';
import { useAnchor } from '@shared/hooks/useAnchor';
import { SERVICES } from '@shared/constants/apiConfig';
import { initiateWSConnection } from '@shared/commonFunctions';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { markAsRead, requestGetUnreadMessages } from '../requests';
import { NotificationsPreview } from './popovers/NotificationsPreview';
import { HeaderControlButton } from './HeaderControlButton';

const pageSize = 20;

export interface INotificationsResponse {
  message: string,
  contactId: string,
  contactName: string,
  createDate: string,
  count: number,
  messageIds: string[],
  senderId: string,
  senderName: string
}

export const SMSNotifications = () => {
  const { userId } = useUserAccount();

  const [anchor, toggleAnchor] = useAnchor();
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [notifications, setNotifications] = useState<INotificationsResponse[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [connection, setConnection] = useState<HubConnection>();

  useEffect(() => {
    getUnreadNotifications();

    const url = `${axios.defaults.baseURL}/${SERVICES.CONTACT}/chat`;
    const initiateAndStartConnection = async () => {
      try {
        // Initiate the WebSocket connection
        const newConnection = await initiateWSConnection(url);

        // Start the connection
        await newConnection.start();

        // Store the connection in the component's state
        setConnection(newConnection);
      } catch (error) {
        console.error('Connection failed: ', error);
      }
    };
    
		// Call the function to initiate and start the connection
    initiateAndStartConnection();

		return () => {
			const connState = 'Connected';
			if (connection && connection.state === connState) {
				setTimeout(() => {
					connection.stop();
				}, 1000);
			}
		};
	}, []);

  const onReceive = useCallback((notifications: {results: INotificationsResponse[], totalCount: number, pageNumber: number}) => {
		setNotifications(notifications.results);
    setTotalCount(notifications.totalCount);
    setPageNumber(notifications.pageNumber);
	}, []);

  useEffect(() => {
    if (connection && connection.state === 'Connected') {
      connection.on('Receive', onReceive);
      connection.invoke('SubscribeByUserNotification', userId);
    }

    return () => {
      if (connection && connection.state === 'Connected' && userId) {
        connection.off('Receive');
        connection.invoke('UnsubscribeFromUserNotification', userId);
      }
    };
  }, [connection, connection?.state, onReceive]);

  const getUnreadNotifications = useCallback(async (resetNotifications = false) => {
    let notificationsResponse : {results: INotificationsResponse[], totalCount: number };
    
    if (resetNotifications) {
      setPageNumber(1);
      notificationsResponse = await requestGetUnreadMessages(1, pageSize);
    } else {
      notificationsResponse = await requestGetUnreadMessages(pageNumber, pageSize);
    }
    if (notificationsResponse) {
      const notificationsData = notificationsResponse.results;
      setTotalCount(notificationsResponse.totalCount);
      setPageNumber(pageNumber + 1);
      if (notificationsData?.length === 0 || notificationsData?.length < 5) {
        setHasMore(false);
      }
      if (resetNotifications) {
        setNotifications([...notificationsData]);
      } else {
        setNotifications([...notifications, ...notificationsData]);
      }
    }
  }, [pageNumber]);

  const markNotificationsAsRead = useCallback(async(selectedNotifications: string[]) => {
    await markAsRead(selectedNotifications);
    toggleAnchor();
    getUnreadNotifications(true);
  },[pageNumber]);

  return (
    <>
      <NotificationsPreview
        anchor={anchor}
        toggleAnchor={toggleAnchor}
        title={`SMS Notifications (${notifications.length})`}
        items={notifications}
        fetchData={getUnreadNotifications}
        markNotificationsAsRead={markNotificationsAsRead}
        hasMore={hasMore}
      />

      <HeaderControlButton count={totalCount} icon={BellIcon} onClick={toggleAnchor} />
    </>
  );
};
